import { ElementIcon, useElementIcon } from "@/components/ui/icons";
import { TreeHeightWrapper } from "@/components/ui/tree/tree-height-wrapper";
import {
  TREE_NODE_HEIGHT,
  TreeNode,
  TreeNodeProps,
} from "@/components/ui/tree/tree-node";
import { useScansExportStore } from "@/modes/export-mode/scans-export/scans-export-store";
import { useIsExportableDataset } from "@/modes/export-mode/scans-export/use-prepare-scans-export-data";
import { useCurrentArea } from "@/modes/mode-data-context";
import { selectIsElementViewable } from "@/modes/mode-selectors";
import { setClippingBox } from "@/store/clipping-box-slice";
import { setIpxPointCloud } from "@/store/modes/ipx-order-mode-slice";
import { setActiveElement } from "@/store/selections-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { elementToTreeData, TreeData } from "@/utils/tree-generation/tree-data";
import {
  blue,
  FaroCheckbox,
  FaroText,
  grey,
  NoTranslate,
  SearchBar,
  TruncatedFaroText,
  useBreakpointMdUp,
} from "@faro-lotv/flat-ui";
import {
  convertToDateString,
  GUID,
  isMacOS,
  walkWithQueue,
} from "@faro-lotv/foundation";
import {
  IElement,
  IElementGenericPointCloudStream,
  isIElementGenericPointCloudStream,
} from "@faro-lotv/ielement-types";
import { selectChildDepthFirst } from "@faro-lotv/project-source";
import { Box, Stack, useMediaQuery } from "@mui/material";
import { isEqual } from "es-toolkit";
import {
  createContext,
  MouseEvent,
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from "react";
import { NodeApi, RowRendererProps, Tree, TreeApi } from "react-arborist";
import { useDragDropManager } from "react-dnd";
import {
  collectRangeScanIds,
  collectScanIds,
  nextIpxScanSelection,
  toggleIpxScanCheckbox,
} from "../ipx-scan-selection";
import { IpxScansDataProvider, useIpxScansData } from "../ipx-scans-data";

const TREE_MIN_HEIGHT = 240;
// Four touch rows, so the step sheet leaves room for the 3D view
const TREE_HEIGHT_SMALL = 176;
const TOUCH_ROW_HEIGHT = 44;

/** The row where a Shift+click range starts, shared by the rows of the tree */
const RangeAnchorContext = createContext<RefObject<GUID | undefined>>({
  current: undefined,
});

/** @returns the viewable point clouds of the current area, as the roots of the tree */
function usePointCloudNodes(): TreeData[] {
  const { dataSessions3d, dataSessions5d } = useCurrentArea();
  const pointCloudId = useAppSelector(
    (state) => state.ipxOrderMode.pointCloudId,
  );
  const isScanSelectionAvailable = useIsExportableDataset();
  const { clusterTree } = useIpxScansData();

  const pointClouds = useAppSelector(
    (state) =>
      [...dataSessions3d.pointClouds, ...dataSessions5d].flatMap((session) => {
        const pointCloud = selectChildDepthFirst(
          session,
          (el: IElement): el is IElementGenericPointCloudStream =>
            isIElementGenericPointCloudStream(el) &&
            selectIsElementViewable(el)(state),
        )(state);
        return pointCloud ? [{ session, pointCloud }] : [];
      }),
    isEqual,
  );

  return useMemo(
    () =>
      pointClouds.map(({ session, pointCloud }) => ({
        ...elementToTreeData({
          element: session,
          id: pointCloud.id,
          label: `${session.name} · ${convertToDateString(session.createdAt)}`,
        }),
        // Only one point cloud is shown at a time, so only its scans are listed
        children:
          pointCloud.id === pointCloudId && isScanSelectionAvailable
            ? clusterTree
            : null,
      })),
    [clusterTree, isScanSelectionAvailable, pointCloudId, pointClouds],
  );
}

/** @returns the point clouds of the area, with the scans of the selected one, to choose what is sent to IPX */
export function ScansPanel(): JSX.Element {
  return (
    <IpxScansDataProvider>
      <ScansPanelContent />
    </IpxScansDataProvider>
  );
}

function ScansPanelContent(): JSX.Element {
  const nodes = usePointCloudNodes();
  const [searchTerm, setSearchTerm] = useState("");
  const isMdUp = useBreakpointMdUp();
  const isTouch = useMediaQuery("(pointer: coarse)");
  // Touch-friendly rows on touch screens
  const rowHeight = isTouch ? TOUCH_ROW_HEIGHT : TREE_NODE_HEIGHT;

  if (!nodes.length) {
    return (
      <FaroText variant="bodyM" color={grey[700]}>
        There are no point clouds in this area.
      </FaroText>
    );
  }

  return (
    <Stack sx={{ gap: 1, flexGrow: 1 }}>
      <SearchBar
        variant="underlined"
        fullWidth
        placeholder="Search point clouds and scans"
        text={searchTerm}
        onTextChanged={setSearchTerm}
        onSearchReset={() => setSearchTerm("")}
      />
      <SelectionNote />
      <Box
        component="div"
        sx={{
          border: `1px solid ${grey[200]}`,
          borderRadius: 1,
          // Takes the room left in the step panel on large screens
          ...(isMdUp && {
            display: "flex",
            flexDirection: "column",
            flex: "1 1 0",
            minHeight: TREE_MIN_HEIGHT,
            overflow: "hidden",
          }),
        }}
      >
        {isMdUp ? (
          <TreeHeightWrapper>
            <PointCloudTree
              nodes={nodes}
              searchTerm={searchTerm}
              rowHeight={rowHeight}
            />
          </TreeHeightWrapper>
        ) : (
          // Shorter on small screens, so the 3D view stays usable above the step sheet
          <PointCloudTree
            nodes={nodes}
            searchTerm={searchTerm}
            height={TREE_HEIGHT_SMALL}
            rowHeight={rowHeight}
          />
        )}
      </Box>
      <FaroText variant="bodyS" color={grey[600]}>
        {isTouch
          ? "Tap the checkboxes to choose the scans and groups to send."
          : `${isMacOS() ? "⌘" : "Ctrl"}+click to add or remove scans or groups, Shift+click to select a range.`}
      </FaroText>
    </Stack>
  );
}

/** @returns why the point cloud can't be split in scans, nothing if it can */
function SelectionNote(): JSX.Element | null {
  if (useIsExportableDataset()) return null;
  return (
    <FaroText variant="bodyS" color={grey[700]} role="status">
      This point cloud can only be sent as a whole.
    </FaroText>
  );
}

type PointCloudTreeProps = {
  /** The point clouds, with the scans of the selected one */
  nodes: TreeData[];

  /** Filter of the visible nodes */
  searchTerm: string;

  /** Height of the tree in pixels, set by the TreeHeightWrapper when it fills its container */
  height?: number;

  /** Height of a row in pixels */
  rowHeight: number;
};

/** @returns the tree of the point clouds, highlighting the selection like the other trees of the Viewer */
function PointCloudTree({
  nodes,
  searchTerm,
  height,
  rowHeight,
}: PointCloudTreeProps): JSX.Element {
  const dragDropManager = useDragDropManager();
  const pointCloudId = useAppSelector(
    (state) => state.ipxOrderMode.pointCloudId,
  );
  const selectedIds = useScansExportStore((state) => state.selectedIds);
  const { invalidClusterIds } = useIpxScansData();
  const isTouch = useMediaQuery("(pointer: coarse)");

  // The highlight follows the selection, that lives in the store of the scans.
  // On touch screens the checkboxes show it
  const treeRef = useRef<TreeApi<TreeData>>(null);
  useEffect(() => {
    if (isTouch) {
      treeRef.current?.setSelection({
        ids: [],
        anchor: null,
        mostRecent: null,
      });
      return;
    }
    const selected = new Set(selectedIds);
    const ids: GUID[] = [];
    walkWithQueue([...nodes], (node, append) => {
      if (node.id === pointCloudId && !selectedIds.length) {
        ids.push(node.id);
        return;
      }
      const scanIds = collectScanIds([node], invalidClusterIds);
      if (node.id !== pointCloudId && scanIds.every((id) => selected.has(id))) {
        if (scanIds.length) ids.push(node.id);
      }
      if (node.children) append(...node.children);
    });
    treeRef.current?.setSelection({ ids, anchor: null, mostRecent: null });
  }, [invalidClusterIds, isTouch, nodes, pointCloudId, selectedIds]);

  // The selected point cloud starts expanded, its clusters collapsed to keep large datasets readable
  const initialOpenState = useMemo(
    () => (pointCloudId ? { [pointCloudId]: true } : undefined),
    [pointCloudId],
  );

  // The row clicked last, where a Shift+click range starts
  const anchor = useRef<GUID>(undefined);

  return (
    <RangeAnchorContext.Provider value={anchor}>
      <Tree<TreeData>
        ref={treeRef}
        dndManager={dragDropManager}
        data={nodes}
        disableDrag
        disableDrop
        width="100%"
        height={height}
        rowHeight={rowHeight}
        indent={20}
        openByDefault={false}
        initialOpenState={initialOpenState}
        searchTerm={searchTerm}
        searchMatch={(node, term) =>
          node.data.label.toLowerCase().includes(term.toLowerCase())
        }
        renderRow={PointCloudTreeRow}
      >
        {PointCloudTreeNode}
      </Tree>
    </RangeAnchorContext.Provider>
  );
}

/**
 * @param node The tree node
 * @returns the scans below the node, whether it's a point cloud, and if it's selected only through its parent or partly
 */
function useNodeSelection(node: NodeApi<TreeData>): {
  scanIds: GUID[];
  isPointCloud: boolean;
  isSelectedIndirectly: boolean;
  isChecked: boolean;
  isPartlyChecked: boolean;
} {
  const { invalidClusterIds } = useIpxScansData();
  const selectedIds = useScansExportStore((state) => state.selectedIds);
  const pointCloudId = useAppSelector(
    (state) => state.ipxOrderMode.pointCloudId,
  );
  return useMemo(() => {
    const scanIds = collectScanIds([node.data], invalidClusterIds);
    const selected = new Set(selectedIds);
    const isPartlySelected =
      scanIds.some((id) => selected.has(id)) &&
      !scanIds.every((id) => selected.has(id));
    // Everything in the shown point cloud is checked while it is sent as a whole
    const isInShownPointCloud = findRoot(node).id === pointCloudId;
    const isWhole = isInShownPointCloud && !selectedIds.length;
    return {
      scanIds,
      isPointCloud: node.level === 0,
      isSelectedIndirectly: hasSelectedAncestor(node) || isPartlySelected,
      isChecked:
        isWhole ||
        (scanIds.length > 0 && scanIds.every((id) => selected.has(id))),
      isPartlyChecked: !isWhole && isPartlySelected,
    };
  }, [invalidClusterIds, node, pointCloudId, selectedIds]);
}

function findRoot(node: NodeApi<TreeData>): NodeApi<TreeData> {
  return node.parent && node.parent.level >= 0 ? findRoot(node.parent) : node;
}

function hasSelectedAncestor({ parent }: NodeApi<TreeData>): boolean {
  return !!parent && (parent.isSelected || hasSelectedAncestor(parent));
}

/** @returns the row of the tree: a click selects only it, Ctrl/Cmd+click adds or removes it, a tap toggles its checkbox */
function PointCloudTreeRow({
  node,
  innerRef,
  attrs,
  children,
}: RowRendererProps<TreeData>): JSX.Element {
  const dispatch = useAppDispatch();
  const pointCloudId = useAppSelector(
    (state) => state.ipxOrderMode.pointCloudId,
  );
  const { invalidClusterIds, clusterTree } = useIpxScansData();
  const { scanIds, isPointCloud } = useNodeSelection(node);
  const { multiSelectIds, multiHoverIds, clearHovering } =
    useScansExportStore();

  const anchor = useContext(RangeAnchorContext);
  const isTouch = useMediaQuery("(pointer: coarse)");
  const select = useCallback(
    (isToggle: boolean, range?: GUID[]) => {
      // Another point cloud is shown and sent as a whole
      if (isPointCloud && node.id !== pointCloudId) {
        useScansExportStore.getState().clearSelection();
        dispatch(setClippingBox(undefined));
        dispatch(setActiveElement(node.id));
        dispatch(setIpxPointCloud(node.id));
        node.open();
        return;
      }
      const allScanIds = collectScanIds(clusterTree, invalidClusterIds);
      const clicked = range ?? (isPointCloud ? allScanIds : scanIds);
      if (!clicked.length) return;
      const { selectedIds } = useScansExportStore.getState();
      multiSelectIds(
        isTouch
          ? toggleIpxScanCheckbox(selectedIds, allScanIds, clicked)
          : nextIpxScanSelection(selectedIds, allScanIds, clicked, isToggle),
      );
    },
    [
      clusterTree,
      dispatch,
      invalidClusterIds,
      isPointCloud,
      isTouch,
      multiSelectIds,
      node,
      pointCloudId,
      scanIds,
    ],
  );
  // Ctrl/Cmd+click adds or removes the item, Shift+click selects the range from the item clicked before
  function onClick(event: MouseEvent): void {
    if (event.shiftKey && anchor.current) {
      const range = collectRangeScanIds(
        node.tree.visibleNodes.map((visible) => visible.data),
        anchor.current,
        node.id,
        invalidClusterIds,
      );
      if (range?.length) {
        select(false, range);
        return;
      }
    }
    anchor.current = node.id;
    select(event.ctrlKey || event.metaKey);
  }

  return (
    <Box
      component="div"
      ref={innerRef}
      {...attrs}
      onClick={onClick}
      onPointerEnter={() => multiHoverIds(scanIds)}
      onPointerLeave={clearHovering}
    >
      {children}
    </Box>
  );
}

function PointCloudTreeNode({
  node,
  style,
}: TreeNodeProps<TreeData>): JSX.Element {
  const icon = useElementIcon(node.data.element, node.data.directParent);
  const {
    scanIds,
    isPointCloud,
    isSelectedIndirectly,
    isChecked,
    isPartlyChecked,
  } = useNodeSelection(node);
  const isTouch = useMediaQuery("(pointer: coarse)");

  return (
    <Box component="div" style={{ ...style, height: "100%" }}>
      <TreeNode<TreeData>
        node={node}
        collapseIcon
        // Clusters without scans that can be ordered
        isDisabled={!isPointCloud && !scanIds.length}
        isSelectedIndirectly={isSelectedIndirectly}
        // Hovered items that are not selected turn blue
        hoverBackgroundColor={blue[100]}
        shouldExpandNodeOnClick={false}
      >
        {isTouch && (
          <FaroCheckbox
            checked={isChecked}
            indeterminate={isPartlyChecked}
            disabled={!isPointCloud && !scanIds.length}
            // The tap is handled by the row, as for the rest of the row
            onChange={noop}
            controlSx={{ m: 0, mr: 0.5 }}
          />
        )}
        {/* Inherit the color of the row, white on the selected one */}
        <ElementIcon
          icon={icon}
          sx={{ fontSize: "1.125em", mr: 0.75, color: "inherit" }}
        />
        <TruncatedFaroText
          variant="bodyM"
          sx={{ color: "inherit" }}
          tooltip={<NoTranslate>{node.data.label}</NoTranslate>}
        >
          <NoTranslate>{node.data.label}</NoTranslate>
        </TruncatedFaroText>
      </TreeNode>
    </Box>
  );
}

function noop(): void {}
